import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  authApi,
  getApiErrorMessage,
  queryKeys,
  authQueries,
} from '@milliswift/api-client'
import { useAuth } from '@milliswift/auth'
import {
  Badge,
  Button,
  Callout,
  ConfirmDialog,
  CopyField,
  Field,
  FormSection,
  Modal,
  OTPInput,
  Stepper,
  useToast,
} from '@milliswift/ui'
import { QRCodeSVG } from 'qrcode.react'
import { AlertTriangle, Monitor, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import type { Session } from '@milliswift/types'

/**
 * Security: MFA enrolment/removal, active sessions, account deletion.
 *
 * Enrolment is a two-step modal — scan, then confirm with a code — and the
 * backup codes returned by the confirm step are shown exactly once in the same
 * dialog, because navigating away from them means losing them.
 */
export default function Security() {
  const { user, confirmMfaEnrollment } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()

  const sessions = useQuery(authQueries.sessions())

  /* ------------------------------------------------------------- MFA enroll */

  const [enrollStep, setEnrollStep] = useState<1 | 2 | 3 | null>(null)
  const [totpUri, setTotpUri] = useState('')
  const [secret, setSecret] = useState('')
  const [enrollCode, setEnrollCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const startEnroll = useMutation({
    mutationFn: () => authApi.enableMFA(),
    onSuccess: (response) => {
      // The platform returns { totp_uri, secret }.
      const uri = (response as { totp_uri?: string }).totp_uri ?? ''
      setSecret((response as { secret?: string }).secret ?? '')
      setTotpUri(uri)
      setEnrollStep(1)
    },
    onError: (error) => toast.error('Could not start enrolment', getApiErrorMessage(error)),
  })

  const verifyEnroll = useMutation({
    mutationFn: (code: string) => confirmMfaEnrollment(code),
    onSuccess: (codes) => {
      setBackupCodes(codes)
      setEnrollStep(3)
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
    },
    onError: (error) => {
      toast.error('That code did not work', getApiErrorMessage(error))
      setEnrollCode('')
    },
  })

  /* ------------------------------------------------------------ MFA disable */

  const [disableCode, setDisableCode] = useState('')

  const disableMfa = useMutation({
    mutationFn: (code: string) => authApi.disableMFA(code),
    onSuccess: () => {
      setConfirmOpen(false)
      setDisableCode('')
      toast.success('Two-factor auth disabled')
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
    },
    onError: (error) => toast.error('Could not disable', getApiErrorMessage(error)),
  })

  /* -------------------------------------------------------------- sessions */

  const revokeSession = useMutation({
    mutationFn: (sessionId: string) => authApi.revokeSession(sessionId),
    onSuccess: () => {
      toast.success('Session revoked')
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessions })
    },
    onError: (error) => toast.error('Could not revoke', getApiErrorMessage(error)),
  })

  /* --------------------------------------------------------------- delete */

  const [deleteOpen, setDeleteOpen] = useState(false)

  const requestDeletion = useMutation({
    mutationFn: () => authApi.requestDeletion(),
    onSuccess: () => {
      setDeleteOpen(false)
      toast.warning('Deletion scheduled', 'You will receive a confirmation email.')
    },
    onError: (error) => toast.error('Could not schedule deletion', getApiErrorMessage(error)),
  })

  /* ------------------------------------------------------------------ view */

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-foreground">Security</h1>
        <p className="mt-1 text-sm text-muted">Two-factor auth, sessions and account lifecycle.</p>
      </div>

      {/* ---------------- MFA ---------------- */}
      <FormSection
        title="Two-factor authentication"
        description="A code from your authenticator app in addition to your password."
        action={
          user?.mfa_enabled ? (
            <Button size="sm" variant="danger" onClick={() => setConfirmOpen(true)}>
              Disable
            </Button>
          ) : (
            <Button
              size="sm"
              loading={startEnroll.isPending}
              onClick={() => startEnroll.mutate()}
            >
              Enable
            </Button>
          )
        }
      >
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-sunken">
            <ShieldCheck className="h-5 w-5 text-accent" aria-hidden />
          </span>
          <div className="space-y-1">
            <Badge tone={user?.mfa_enabled ? 'success' : 'warning'} dot>
              {user?.mfa_enabled ? 'Enabled' : 'Not enabled'}
            </Badge>
            <p className="text-caption text-muted">
              {user?.mfa_enabled
                ? 'Your account requires a code at every sign-in.'
                : 'Without two-factor, a leaked password alone is enough to sign in.'}
            </p>
          </div>
        </div>
      </FormSection>

      {/* ---------------- sessions ---------------- */}
      <FormSection
        title="Active sessions"
        description="Devices where you are currently signed in."
      >
        {sessions.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : sessions.isError ? (
          <Callout tone="danger">{getApiErrorMessage(sessions.error)}</Callout>
        ) : (sessions.data ?? []).length === 0 ? (
          <Callout tone="info">No other sessions.</Callout>
        ) : (
          <ul className="divide-y divide-line">
            {(sessions.data ?? []).map((session: Session) => (
              <li key={session.id} className="flex flex-wrap items-center gap-3 py-3 first:pt-0 last:pb-0">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sunken">
                  <Monitor className="h-4 w-4 text-muted" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {session.user_agent || 'Unknown client'}
                  </p>
                  <p className="text-caption text-muted">
                    {session.ip} · last active {new Date(session.last_used_at).toLocaleString()}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  loading={revokeSession.isPending && revokeSession.variables === session.id}
                  onClick={() => revokeSession.mutate(session.id)}
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
      </FormSection>

      {/* ---------------- danger ---------------- */}
      <div className="rounded-card border border-danger/30 bg-surface p-5">
        <h2 className="text-sm font-semibold text-danger-text">Danger zone</h2>
        <p className="mt-1 text-caption text-muted">
          Scheduling deletion starts a grace period; data is destroyed after it ends.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button variant="danger" size="sm" onClick={() => setDeleteOpen(true)}>
            Delete account
          </Button>
        </div>
      </div>

      {/* ---------------- modals ---------------- */}

      {/* MFA enrolment */}
      <Modal
        open={enrollStep !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEnrollStep(null)
            setEnrollCode('')
            setBackupCodes(null)
          }
        }}
        title="Set up two-factor authentication"
        size="md"
      >
        <div className="space-y-6">
          <Stepper
            current={(enrollStep ?? 1) - 1}
            steps={[
              { label: 'Scan the code' },
              { label: 'Confirm with a code' },
              { label: 'Save backup codes' },
            ]}
          />

          {enrollStep === 1 ? (
            <div className="flex flex-col items-center gap-4 text-center">
              {totpUri ? (
                <div className="rounded-card border border-line bg-white p-3">
                  <QRCodeSVG value={totpUri} size={160} />
                </div>
              ) : null}
              <p className="text-sm text-muted">
                Scan with your authenticator app, or enter the key manually.
              </p>
              {secret ? <CopyField label="Manual key" value={secret} /> : null}
              <Button onClick={() => setEnrollStep(2)}>I've scanned it</Button>
            </div>
          ) : null}

          {enrollStep === 2 ? (
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="text-sm text-muted">
                Enter the 6-digit code your app shows for milliswift.
              </p>
              <OTPInput
                value={enrollCode}
                onValueChange={setEnrollCode}
                onComplete={(code) => verifyEnroll.mutate(code)}
                autoFocus
                disabled={verifyEnroll.isPending}
              />
              <Button
                disabled={enrollCode.length !== 6}
                loading={verifyEnroll.isPending}
                onClick={() => verifyEnroll.mutate(enrollCode)}
              >
                Confirm
              </Button>
            </div>
          ) : null}

          {enrollStep === 3 && backupCodes ? (
            <div className="space-y-4">
              <Callout tone="warning" title="Save these now" icon={<AlertTriangle className="h-4 w-4" />}>
                Each code works once, and this is the only time they are shown.
              </Callout>
              <div className="grid grid-cols-2 gap-2 rounded-card border border-line bg-sunken p-4 font-mono text-caption sm:grid-cols-3">
                {backupCodes.map((code) => (
                  <span key={code} className="text-foreground">
                    {code}
                  </span>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void navigator.clipboard.writeText(backupCodes.join('\n')).catch(() => undefined)
                    toast.success('Copied all codes')
                  }}
                >
                  Copy all
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain' })
                    const url = URL.createObjectURL(blob)
                    const anchor = document.createElement('a')
                    anchor.href = url
                    anchor.download = 'milliswift-backup-codes.txt'
                    anchor.click()
                    URL.revokeObjectURL(url)
                  }}
                >
                  Download
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setEnrollStep(null)
                    setEnrollCode('')
                    setBackupCodes(null)
                    toast.success('Two-factor auth enabled')
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </Modal>

      {/* MFA disable */}
      <Modal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Disable two-factor authentication?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={disableMfa.isPending}
              disabled={disableCode.length !== 6}
              onClick={() => disableMfa.mutate(disableCode)}
            >
              Disable
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Callout tone="warning" icon={<AlertTriangle className="h-4 w-4" />}>
            Your password alone will be enough to sign in.
          </Callout>
          <Field label="Enter a current code to confirm">
            <OTPInput
              value={disableCode}
              onValueChange={setDisableCode}
              disabled={disableMfa.isPending}
            />
          </Field>
        </div>
      </Modal>

      {/* Deletion */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Schedule account deletion?"
        description="A grace period begins; you can cancel until it ends."
        confirmLabel="Schedule deletion"
        destructive
        loading={requestDeletion.isPending}
        onConfirm={() => requestDeletion.mutate()}
      >
        <Callout tone="danger" icon={<AlertTriangle className="h-4 w-4" />}>
          All databases, snapshots and API keys will be permanently destroyed.
        </Callout>
      </ConfirmDialog>
    </div>
  )
}
